import React, { useState, useEffect } from 'react';
import {
  Sparkles, Zap, Wand2, Film, Video, Image,
  CheckCircle, Loader2, Clock, Lightbulb
} from 'lucide-react';

/**
 * Mensagens contextuais por tipo de conteúdo
 */
const LOADING_MESSAGES = {
  image: [
    { message: "Preparando paleta de cores...", duration: 2000, icon: Sparkles },
    { message: "Analisando seu briefing...", duration: 2500, icon: Lightbulb },
    { message: "Criando composição visual...", duration: 3000, icon: Image },
    { message: "Ajustando detalhes...", duration: 2000, icon: Sparkles },
    { message: "Quase lá! Finalizando sua imagem...", duration: 2500, icon: CheckCircle }
  ],
  video: [
    { message: "Enviando tarefa de vídeo... (isso é rápido!)", duration: 2000, icon: Zap },
    { message: "Seu vídeo está na fila de processamento", duration: 2000, icon: Clock },
    { message: "A criação acontecerá em background", duration: 2000, icon: Video },
    { message: "Você será notificado quando estiver pronto!", duration: 1500, icon: CheckCircle }
  ],
  mixed: [
    { message: "Preparando imagens e vídeos...", duration: 2000, icon: Sparkles },
    { message: "Imagens: criação imediata", duration: 2000, icon: Image },
    { message: "Vídeos: processamento em background", duration: 2000, icon: Film },
    { message: "Analisando suas preferências...", duration: 2500, icon: Lightbulb },
    { message: "Criando suas imagens agora...", duration: 3000, icon: Zap },
    { message: "Quase pronto!", duration: 2000, icon: CheckCircle }
  ]
};

/**
 * Animação de Partículas
 */
const FloatingParticle = ({ delay, duration, size }) => (
  <div
    className="absolute rounded-full bg-amber-400 opacity-10 animate-float"
    style={{
      width: `${size}px`,
      height: `${size}px`,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`
    }}
  />
);

/**
 * Ícone Animado Central
 */
const AnimatedCenterIcon = ({ type }) => {
  const icons = [Sparkles, Wand2, Image, Film, Video, Zap];
  const [currentIconIndex, setCurrentIconIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIconIndex((prev) => (prev + 1) % icons.length);
    }, 1500);
    
    return () => clearInterval(interval);
  }, []);

  const CurrentIcon = icons[currentIconIndex];

  return (
    <div className="relative w-24 h-24 animate-pulse">
      {/* Círculo externo */}
      <div className="absolute inset-0 rounded-full bg-amber-200 opacity-50 animate-ping" />
      {/* Círculo principal */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center shadow-xl">
        <CurrentIcon className="w-12 h-12 text-white animate-spin-slow" />
      </div>
    </div>
  );
};

/**
 * Barra de Progresso
 */
const AnimatedProgressBar = ({ progress }) => (
  <div className="w-full max-w-2xl">
    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-1000 ease-out relative overflow-hidden"
        style={{ width: `${progress}%` }}
      >
        {/* Shimmer effect sutil */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
      </div>
    </div>
    <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
      <span>Criando...</span>
      <span className="font-semibold text-amber-600">{Math.round(progress)}%</span>
    </div>
  </div>
);

/**
 * Timeline de Steps
 */
const StepTimeline = ({ steps, currentStep }) => (
  <div className="space-y-2.5 w-full max-w-lg">
    {steps.map((step, idx) => {
      const Icon = step.icon;
      const isCompleted = idx < currentStep;
      const isCurrent = idx === currentStep;
      const isPending = idx > currentStep;

      return (
        <div
          key={idx}
          className={`flex items-center gap-3 transition-all duration-500 ${
            isPending ? 'opacity-30' : 'opacity-100'
          }`}
        >
          {/* Ícone */}
          <div
            className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
              isCompleted
                ? 'bg-green-500'
                : isCurrent
                ? 'bg-gradient-to-r from-amber-400 to-orange-500 animate-pulse'
                : 'bg-gray-200'
            }`}
          >
            {isCompleted ? (
              <CheckCircle className="w-4 h-4 text-white" />
            ) : isCurrent ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Icon className="w-4 h-4 text-gray-400" />
            )}
          </div>

          {/* Mensagem */}
          <div className="flex-1">
            <p
              className={`font-medium transition-all text-sm ${
                isCurrent
                  ? 'text-gray-900'
                  : isCompleted
                  ? 'text-gray-600'
                  : 'text-gray-400'
              }`}
            >
              {step.message}
            </p>
          </div>
        </div>
      );
    })}
  </div>
);

/**
 * Componente Principal
 */
const CreationLoadingScreen = ({ 
  contentType = 'image', // 'image', 'video', 'mixed'
  onComplete 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [randomTip, setRandomTip] = useState('');

  const steps = LOADING_MESSAGES[contentType] || LOADING_MESSAGES.image;

  // Tips aleatórios
  const tips = [
    "Você sabia? A IA analisa milhões de referências para criar seu conteúdo!",
    "Dica: Quanto mais específico o briefing, melhor o resultado!",
    "A mágica está acontecendo... cada pixel é cuidadosamente criado.",
    "Enquanto espera, que tal preparar uma xícara de café?",
    "Nossos modelos de IA foram treinados com bilhões de imagens!",
    "Você pode criar até 8 imagens por vez com o Flux-1.1!",
    "Vídeos são processados em background para você não perder tempo."
  ];

  // Inicializar tip aleatório
  useEffect(() => {
    setRandomTip(tips[Math.floor(Math.random() * tips.length)]);
  }, []);

  // Avançar steps automaticamente
  useEffect(() => {
    if (currentStep >= steps.length - 1) {
      if (onComplete) {
        setTimeout(onComplete, 1000);
      }
      return;
    }

    const timer = setTimeout(() => {
      setCurrentStep((prev) => prev + 1);
    }, steps[currentStep].duration);

    return () => clearTimeout(timer);
  }, [currentStep, steps, onComplete]);

  // Animar progresso
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        const targetProgress = ((currentStep + 1) / steps.length) * 100;
        const increment = (targetProgress - prev) / 10;
        return Math.min(prev + increment, targetProgress);
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentStep, steps.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Partículas de fundo sutis */}
      {[...Array(10)].map((_, i) => (
        <FloatingParticle
          key={i}
          delay={i * 0.5}
          duration={10 + Math.random() * 10}
          size={20 + Math.random() * 40}
        />
      ))}

      {/* Container Central */}
      <div className="relative z-10 max-w-3xl w-full">
        {/* Card Principal */}
        <div className="bg-white border-2 border-gray-200 rounded-3xl shadow-xl p-10 text-center">
          {/* Título */}
          <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-3">
            <Sparkles className="w-8 h-8 text-amber-500" />
            Mágica Acontecendo!
          </h2>
          
          {/* Subtítulo */}
          <p className="text-base text-gray-600 mb-8">
            Criando conteúdos incríveis para você...
          </p>

          {/* Ícone Central Animado */}
          <div className="flex justify-center mb-8">
            <AnimatedCenterIcon type={contentType} />
          </div>

          {/* Timeline de Steps */}
          <div className="flex justify-center mb-6">
            <StepTimeline steps={steps} currentStep={currentStep} />
          </div>

          {/* Barra de Progresso */}
          <div className="flex justify-center mb-6">
            <AnimatedProgressBar progress={progress} />
          </div>

          {/* Tip Aleatório */}
          <div className="rounded-xl p-3 inline-flex items-start gap-2 max-w-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
            <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
            <p className="text-xs text-amber-800 text-left">
              <strong className="font-semibold">Dica:</strong> {randomTip}
            </p>
          </div>

          {/* Nota de Tempo */}
          <p className="text-xs text-gray-500 mt-6">
            Aguarde, estamos trabalhando duro para você...
          </p>
        </div>

        {/* Badge de Estimativa */}
        {contentType === 'video' && (
          <div className="mt-4 text-center">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-md">
              <Clock className="w-4 h-4" />
              Vídeos processam em background - você será notificado!
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreationLoadingScreen;